import { useEffect, useState } from "react"
import { useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import { Box, IconButton, Paper, TableContainer, Tooltip } from "@mui/material"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { DataGrid, GridColDef, GridFooterContainer, GridPagination } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { useNavigate } from "react-router-dom"
import { APIError } from "../../app/appTypes"
import { AddFabButton } from "../../app/components/AddFabButton"
import { ErrorContainer } from "../../app/ErrorContainer"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { LoadingContainer } from "../../app/LoadingContainer"
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar"
import { selectSettings } from "../settings/settingsSlice"
import { showError } from "../notifications/notificationsSlice"
import { DeleteConfirmationDialog } from "../utils/DeleteConfirmationDialog"
import { CreateEditOperationalDashboard } from "./dialogs/CreateEditOperationalDashboard"
import { deactivateOperationalDashboard, loadOperationalDashboards, OperationalDashboardDto } from "./operationalDashboards"

type DashboardDialog =
    | { mode: "create" }
    | { mode: "edit"; dashboard: OperationalDashboardDto }
    | null

export const OperationalDashboardsList = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const [dashboards, setDashboards] = useState<OperationalDashboardDto[]>([])
    const [dialog, setDialog] = useState<DashboardDialog>(null)
    const [dashboardToDeactivate, setDashboardToDeactivate] = useState<OperationalDashboardDto | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isError, setIsError] = useState(false)
    const [error, setError] = useState<APIError | undefined>()

    useEffect(() => {
        dispatch(loadOperationalDashboards({ includeInactive: true }))
            .then(unwrapResult)
            .then(setDashboards)
            .catch((error: APIError) => {
                console.error(error)
                setError(error)
                setIsError(true)
            })
            .finally(() => setIsLoading(false))
    }, [dispatch])

    const handleSuccessfullySaved = (dashboard: OperationalDashboardDto) => {
        setDashboards(prev => {
            const exists = prev.some(item => item.id === dashboard.id)
            return exists
                ? prev.map(item => item.id === dashboard.id ? dashboard : item)
                : [...prev, dashboard]
        })
        setDialog(null)
    }

    const handleDeactivate = async (id: number) => {
        const result = await dispatch(deactivateOperationalDashboard(id))
        if (deactivateOperationalDashboard.fulfilled.match(result)) {
            setDashboards(prev => prev.map(dashboard =>
                dashboard.id === id ? result.payload : dashboard
            ))
            setDashboardToDeactivate(null)
        } else {
            dispatch(showError(result.payload?.kind === "http"
                ? result.payload.problem?.title ?? "error"
                : "error"))
        }
    }

    const columns: GridColDef<OperationalDashboardDto>[] = [
        { field: "name", headerName: l10n.getString("name", null, "_Nazwa"), flex: 1, minWidth: 200 },
        { field: "description", headerName: l10n.getString("description", null, "_Opis"), flex: 1, minWidth: 200 },
        { field: "isActive", headerName: l10n.getString("active", null, "_Aktywne"), type: "boolean", width: 120 },
        { field: "ownerName", headerName: l10n.getString("owner", null, "_Właściciel"), width: 180 },
        { field: "isPublic", headerName: l10n.getString("operational-dashboard-is-public", null, "_Publiczny"), type: "boolean", width: 120 },
        {
            field: "actions",
            headerName: l10n.getString("actions", null, "_Akcje"),
            width: 150,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: params => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Tooltip title={l10n.getString("operational-dashboard-view", null, "_Zobacz dashboard")}>
                        <IconButton
                            size="small"
                            aria-label={l10n.getString("operational-dashboard-view", null, "_Zobacz dashboard")}
                            onClick={() => navigate(`/operational-dashboards/view/${params.row.id}`)}
                        >
                            <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={l10n.getString("operational-dashboard-configure", null, "_Konfiguruj dashboard")}>
                        <IconButton
                            size="small"
                            aria-label={l10n.getString("operational-dashboard-configure", null, "_Konfiguruj dashboard")}
                            onClick={() => setDialog({ mode: "edit", dashboard: params.row })}
                        >
                            <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={l10n.getString("delete", null, "_Usuń")}>
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                aria-label={l10n.getString("delete", null, "_Usuń")}
                                disabled={!params.row.isActive}
                                onClick={() => setDashboardToDeactivate(params.row)}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ]

    if (isLoading) return <LoadingContainer />
    if (isError) return <ErrorContainer error={error} />

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid
                    localeText={locale === "pl"
                        ? plPL.components.MuiDataGrid.defaultProps.localeText
                        : enUS.components.MuiDataGrid.defaultProps.localeText}
                    rows={dashboards}
                    columns={columns}
                    style={{ minHeight: 100, height: "calc(100vh - 150px)" }}
                    slots={{
                        toolbar: QuickSearchToolbar,
                        footer: () => (
                            <GridFooterContainer sx={{ justifyContent: "space-between" }}>
                                <Tooltip title={l10n.getString("operational-dashboard-create", null, "_Dodaj dashboard operacyjny")}>
                                    <span><AddFabButton onClick={() => setDialog({ mode: "create" })} /></span>
                                </Tooltip>
                                <GridPagination />
                            </GridFooterContainer>
                        ),
                    }}
                    initialState={{
                        sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                    }}
                />
            </TableContainer>
            {dialog?.mode === "create" && (
                <CreateEditOperationalDashboard
                    mode="create"
                    onSuccessfullySaved={handleSuccessfullySaved}
                    onCancel={() => setDialog(null)}
                />
            )}
            {dialog?.mode === "edit" && (
                <CreateEditOperationalDashboard
                    mode="edit"
                    dashboard={dialog.dashboard}
                    onSuccessfullySaved={handleSuccessfullySaved}
                    onCancel={() => setDialog(null)}
                />
            )}
            {dashboardToDeactivate && (
                <DeleteConfirmationDialog
                    open
                    onConfirm={() => handleDeactivate(dashboardToDeactivate.id)}
                    onCancel={() => setDashboardToDeactivate(null)}
                />
            )}
        </>
    )
}